import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@shared/api';
import { ModelLogo, ProviderLogo, providerName } from '@shared/brand';
import { Icon, InfoTip } from './ui';
import { MODALITY_LABEL, modalityOf, providerOf, type CatalogModel } from '../catalog';

let catalogCache: Promise<CatalogModel[]> | null = null;

/** Cached model list. */
export function useCatalogModels(): CatalogModel[] | null {
  const [models, setModels] = useState<CatalogModel[] | null>(null);
  useEffect(() => {
    let alive = true;
    catalogCache ??= api<{ data: CatalogModel[] }>('/v1/models')
      .then((body) => body.data)
      .catch(() => {
        catalogCache = null;
        return [];
      });
    void catalogCache.then((list) => alive && setModels(list));
    return () => {
      alive = false;
    };
  }, []);
  return models;
}

/** Order-insensitive compare. */
export function sameModels(a: string[] | null, b: string[] | null): boolean {
  if (!a || !b) return a === b;
  return a.length === b.length && a.every((slug) => b.includes(slug));
}

export function ModelAccessSummary({
  allowed,
  models,
  max = 3,
}: {
  allowed: string[] | null;
  models: Map<string, CatalogModel>;
  max?: number;
}) {
  if (!allowed) {
    return (
      <span className="ma-all">
        <Icon name="grid" size={14} />
        Semua model
      </span>
    );
  }
  const shown = allowed.slice(0, max);
  const rest = allowed.length - shown.length;
  const single = allowed.length === 1 ? models.get(allowed[0]) : undefined;
  return (
    <InfoTip
      label={`Hanya ${allowed.length} model: ${allowed.map((slug) => models.get(slug)?.display_name ?? slug).join(', ')}`}
      width={300}
      trigger={
        <span className="ma-sum">
          <span className="ma-stack">
            {shown.map((slug) => {
              const m = models.get(slug);
              return <ModelLogo key={slug} model={slug} family={m?.family} provider={m ? providerOf(m) : undefined} size={22} />;
            })}
          </span>
          {rest > 0 && <span className="ma-more">+{rest}</span>}
          {allowed.length === 1 && <span className="ma-name truncate">{single?.display_name ?? allowed[0]}</span>}
        </span>
      }
    >
      <div className="ma-tip-title">Hanya {allowed.length} model ini</div>
      <ul className="ma-tip-list">
        {allowed.map((slug) => {
          const m = models.get(slug);
          return (
            <li key={slug}>
              <ModelLogo model={slug} family={m?.family} provider={m ? providerOf(m) : undefined} size={18} />
              <span className="truncate">{m?.display_name ?? slug}</span>
            </li>
          );
        })}
      </ul>
    </InfoTip>
  );
}

export function ModelAccessField({
  models,
  value,
  onChange,
  idPrefix = 'ma',
}: {
  models: CatalogModel[] | null;
  /** null = all models. */
  value: string[] | null;
  onChange: (next: string[] | null) => void;
  idPrefix?: string;
}) {
  const remembered = useRef<string[]>(value ?? []);
  if (value) remembered.current = value;
  const some = value !== null;

  return (
    <div className="field">
      <span className="label label-tip">
        Akses model
        <InfoTip label="Tentang akses model">
          <p>Batasi model yang boleh dipanggil key ini, misalnya hanya model murah untuk chatbot publik.</p>
          <p>Model lain ditolak dengan error <code>model_not_allowed</code>. Router <code>auto:</code> dan model cadangan juga hanya memilih dari daftar ini.</p>
        </InfoTip>
      </span>
      <div className="ma-choice" role="radiogroup" aria-label="Akses model">
        <label className={some ? 'ma-opt' : 'ma-opt on'}>
          <input type="radio" name={`${idPrefix}-access`} checked={!some} onChange={() => onChange(null)} />
          <span className="ma-opt-text">
            <span className="ma-opt-title">Semua model</span>
            <span className="ma-opt-sub">Termasuk model baru nanti</span>
          </span>
        </label>
        <label className={some ? 'ma-opt on' : 'ma-opt'}>
          <input type="radio" name={`${idPrefix}-access`} checked={some} onChange={() => onChange([...remembered.current])} />
          <span className="ma-opt-text">
            <span className="ma-opt-title">Hanya model tertentu</span>
            <span className="ma-opt-sub">Model lain ditolak</span>
          </span>
        </label>
      </div>
      {some &&
        (models === null ? (
          <div className="ma-box ma-loading">
            <span className="spinner" /> Memuat model…
          </div>
        ) : (
          <ModelChecklist models={models} selected={value} onChange={onChange} idPrefix={idPrefix} />
        ))}
    </div>
  );
}

function ModelChecklist({
  models,
  selected,
  onChange,
  idPrefix,
}: {
  models: CatalogModel[];
  selected: string[];
  onChange: (next: string[]) => void;
  idPrefix: string;
}) {
  const [query, setQuery] = useState('');
  const bySlug = useMemo(() => new Map(models.map((m) => [m.id, m])), [models]);
  const chosen = new Set(selected);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const map = new Map<string, CatalogModel[]>();
    for (const m of models) {
      const provider = providerOf(m);
      if (q && !`${m.id} ${m.display_name} ${providerName(provider)}`.toLowerCase().includes(q)) continue;
      map.set(provider, [...(map.get(provider) ?? []), m]);
    }
    return [...map.entries()].map(([provider, items]) => ({ provider, items }));
  }, [models, query]);

  const toggle = (slug: string) => onChange(chosen.has(slug) ? selected.filter((s) => s !== slug) : [...selected, slug]);

  const toggleGroup = (items: CatalogModel[]) => {
    const all = items.every((m) => chosen.has(m.id));
    if (all) onChange(selected.filter((slug) => !items.some((m) => m.id === slug)));
    else onChange([...selected, ...items.map((m) => m.id).filter((slug) => !chosen.has(slug))]);
  };

  return (
    <div className="ma-box">
      <div className="ma-bar">
        <div className="search grow">
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari model atau provider" aria-label="Cari model" />
        </div>
        <span className="ma-bar-meta">
          <span className={selected.length ? 'ma-count on' : 'ma-count'} aria-live="polite">
            {selected.length} dipilih
          </span>
          {selected.length > 0 && (
            <button type="button" className="btn btn-sm btn-ghost" onClick={() => onChange([])}>
              Kosongkan
            </button>
          )}
        </span>
      </div>

      {selected.length > 0 && (
        <div className="ma-chips">
          {selected.map((slug) => {
            const m = bySlug.get(slug);
            return (
              <span key={slug} className="ma-chip">
                <ModelLogo model={slug} family={m?.family} provider={m ? providerOf(m) : undefined} size={18} />
                <span className="truncate">{m?.display_name ?? slug}</span>
                <button type="button" onClick={() => toggle(slug)} aria-label={`Hapus ${m?.display_name ?? slug}`}>
                  <Icon name="close" size={13} />
                </button>
              </span>
            );
          })}
        </div>
      )}

      <div className="ma-list">
        {groups.length === 0 && <div className="ma-empty">Tidak ada model yang cocok dengan “{query}”.</div>}
        {groups.map((group) => {
          const picked = group.items.filter((m) => chosen.has(m.id)).length;
          const all = picked === group.items.length;
          return (
            <div key={group.provider} className="ma-group" role="group" aria-label={providerName(group.provider)}>
              <div className="ma-group-head">
                <ProviderLogo provider={group.provider} size={16} bare />
                <span className="ma-group-name">{providerName(group.provider)}</span>
                <span className="ma-group-n">
                  {picked}/{group.items.length}
                </span>
                <span className="grow" />
                <button type="button" className="ma-link" onClick={() => toggleGroup(group.items)}>
                  {all ? 'Batalkan semua' : 'Pilih semua'}
                </button>
              </div>
              {group.items.map((m) => {
                const on = chosen.has(m.id);
                const mode = modalityOf(m);
                return (
                  <label key={m.id} className={on ? 'ma-row on' : 'ma-row'} htmlFor={`${idPrefix}-${m.id}`}>
                    <input id={`${idPrefix}-${m.id}`} type="checkbox" checked={on} onChange={() => toggle(m.id)} />
                    <ModelLogo model={m.id} family={m.family} provider={providerOf(m)} size={26} />
                    <span className="ma-row-text">
                      <span className="ma-row-name">{m.display_name}</span>
                      <span className="ma-row-id">{m.id}</span>
                    </span>
                    {mode !== 'text' && <span className="tag">{MODALITY_LABEL[mode]}</span>}
                  </label>
                );
              })}
            </div>
          );
        })}
      </div>
      {selected.length === 0 && <div className="ma-hint">Centang minimal satu model.</div>}
    </div>
  );
}
